import React, {useState} from 'react';
import {DateTime} from 'luxon';
import Button from '@/components/ui/Button';
import {ModalAlert} from '@/components/alerts';
import axios from 'axios';
import useData from '@/utils/useData';
import CellLoader from '@/components/loading/CellLoader';
import {buildURL} from '@/utils/urlBuilder';
import TableComponent from '@/components/table/Table';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';

interface TranscriptionLog {
    id: number;
    eventLang: string;
    createdOn: string;
    type: string;
    eventCallId: number;
    externalTaskId: string;
    status: string;
}

interface TranscriptionLogsTableProps {
    eventCallId: number;
    isAuditView?: boolean;
}

const TranscriptionLogsTable: React.FC<TranscriptionLogsTableProps> = React.memo((
    {
        eventCallId,
        isAuditView = false,
    },
) => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
    const [transcriptionToDelete, setTranscriptionToDelete] = useState<{id: number, externalTaskId: string} | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [transcriptionLogs, loading] = useData<TranscriptionLog[]>(
        buildURL('transcriptionTasksDetails', {event_call_id: eventCallId}), 'eventDetail', 'setEventCallTranscriptionTasks', 'eventCallTranscriptionTasks',
    );
    

    if (loading) {
        return (
            <CellLoader/>
        );
    }

    if (transcriptionLogs.length === 0) {
        return (
            <span className="m-3">
                {i18n.t('jt_data_available.text')}
            </span>
        );
    }

    const openDeleteConfirm = (transcriptionId: number, externalTaskId: string): void => {
        setTranscriptionToDelete({id: transcriptionId, externalTaskId});
        setDeleteConfirmOpen(true);
    };

    const closeDeleteConfirm = (): void => {
        setTranscriptionToDelete(null);
        setDeleteConfirmOpen(false);
        setIsDeleting(false);
    };

    const confirmDelete = async (): Promise<void> => {
        if (!transcriptionToDelete) {return;}

        setIsDeleting(true);
        try {
            const url = buildURL('transcriptionTaskDetails', {transcription_id: transcriptionToDelete.id});
            await axios.delete(url);
            closeDeleteConfirm();
        } catch (error) {
            setIsDeleting(false);
            throw error;
        }
    };

    const deleteTranscription = (transcriptionId: number, externalTaskId: string): void => {
        openDeleteConfirm(transcriptionId, externalTaskId);
    };

    const columns = [
        !isAuditView && {
            header: i18n.t('th_event_lang.text'),
            accessorKey: 'eventLang',
            cell: (cell: { row: { original: TranscriptionLog } }) => {
                return cell.row.original.eventLang || '-';
            },
        },
        {
            header: i18n.t('th_created.text'),
            accessorKey: 'createdOn',
            cell: (cell: { row: { original: TranscriptionLog } }) => {
                return configLoaded ? formatDateTime(cell.row.original.createdOn, true, {setZone: false},
                ) : '';
            },
            sortValue: (cell: string) => {
                if (cell === '-') {
                    return cell;
                }

                return DateTime.fromISO(cell);
            },
        },
        !isAuditView && {
            header: i18n.t('th_type.text'),
            accessorKey: 'type',
        },
        !isAuditView && {
            header: i18n.t('th_event_call.text'),
            accessorKey: 'eventCallId',
        },
        {
            header: i18n.t('th_external_task.text'),
            accessorKey: 'externalTaskId',
            sort: true,
        },
        {
            header: i18n.t('th_status.text'),
            accessorKey: 'status',
        },
        {
            accessorKey: i18n.t('th_date_created.text'),
            cell: (cell: { row: { original: TranscriptionLog } }) => (
                <div className="d-inline-block">
                    <Button
                        variant="danger"
                        className="d-inline-block"
                        onClick={() => deleteTranscription(cell.row.original.id, cell.row.original.externalTaskId)}
                    >
                        {i18n.t('jt_delete.text')}
                    </Button>
                </div>
            ),
        },
    ].filter(Boolean);

    return (
        <>
            <div className="card">
                <div className="card-body">
                    <TableComponent data={transcriptionLogs} columns={columns}/>
                </div>
            </div>
            <ModalAlert
                isOpen={deleteConfirmOpen}
                onClose={closeDeleteConfirm}
                variant="error"
                title={i18n.t('jt_confirm_deletion.text')}
                message={i18n.t('es_are_you_sure_delete_task.text', {externalTaskId: transcriptionToDelete?.externalTaskId})}
                actions={[
                    {
                        label: i18n.t('jt_delete.text'),
                        onClick: confirmDelete,
                        variant: 'danger',
                        loading: isDeleting
                    }
                ]}
                dismissible={!isDeleting}
            />
        </>
    );
});


export default TranscriptionLogsTable; 